import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  LinearProgress,
  Alert,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import WorkIcon from '@mui/icons-material/Work';
import PaymentIcon from '@mui/icons-material/Payment';
import ContactMailIcon from '@mui/icons-material/ContactMail';

import { EmployeeService, SalaryProfileService, PayrollItemService } from '../../services/payrollService';
import { Employee, SalaryProfile, PayrollItem } from '../../types/payroll';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { useRole } from '../../hooks/useRole';
import ConfirmDialog from '../common/ConfirmDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`employee-tabpanel-${index}`}
      aria-labelledby={`employee-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `employee-tab-${index}`,
    'aria-controls': `employee-tabpanel-${index}`,
  };
};

const EmployeeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasAnyRole, canAccessEmployeeData } = useRole();
  
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [salaryProfiles, setSalaryProfiles] = useState<SalaryProfile[]>([]);
  const [payrollHistory, setPayrollHistory] = useState<PayrollItem[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  // Determine user permissions
  const isHR = hasAnyRole(['hr', 'admin']);
  const isPayroll = hasAnyRole(['payroll', 'admin']);
  
  // Check if user has access to this employee
  const hasAccess = employee ? canAccessEmployeeData(employee.id) : true;
  
  // Load employee data
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        
        // Fetch employee details
        const employeeResponse = await EmployeeService.getEmployeeById(Number(id));
        setEmployee(employeeResponse.data);
        
        // Fetch salary profiles for this employee
        const profilesResponse = await SalaryProfileService.getAllForEmployee(Number(id));
        setSalaryProfiles(profilesResponse.data);
        
        // Fetch payroll history for this employee
        const payrollItemsResponse = await PayrollItemService.getPayrollItemsForEmployee(Number(id));
        setPayrollHistory(payrollItemsResponse.data);
        
        setError(null);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load employee data');
        console.error('Error fetching employee data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  
  // Handle back navigation
  const handleBack = () => {
    navigate('/payroll/employees');
  };
  
  // Handle edit employee
  const handleEditEmployee = () => {
    navigate(`/payroll/employees/edit/${id}`);
  };
  
  // Handle delete employee
  const handleDeleteEmployee = () => {
    setDeleteDialogOpen(true);
  };
  
  // Handle confirm delete
  const handleConfirmDelete = async () => {
    if (!id || !isHR) return;
    
    try {
      setLoading(true);
      await EmployeeService.deleteEmployee(Number(id));
      
      // Navigate back to employee list after successful deletion
      navigate('/payroll/employees', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete employee');
      console.error('Error deleting employee:', err);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };
  
  // Handle cancel delete
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
  };
  
  // Handle add salary profile
  const handleAddSalaryProfile = () => {
    navigate(`/payroll/salary-profiles/new?employeeId=${id}`);
  };
  
  // Get current active salary profile
  const getCurrentSalaryProfile = (): SalaryProfile | undefined => {
    if (!salaryProfiles.length) return undefined;
    
    const today = new Date();
    
    // Find active profile (effective_from <= today && (effective_to is null or effective_to >= today))
    return salaryProfiles.find(profile => {
      const effectiveFrom = new Date(profile.effective_from);
      const effectiveTo = profile.effective_to ? new Date(profile.effective_to) : null;
      
      return effectiveFrom <= today && (!effectiveTo || effectiveTo >= today);
    });
  };
  
  // Get contract type display text
  const getContractTypeDisplay = (type: string): string => {
    const types: Record<string, string> = {
      'FULL_TIME': 'Full Time',
      'PART_TIME': 'Part Time',
      'CONTRACTOR': 'Contractor',
      'PROBATION': 'Probation',
      'INTERN': 'Intern'
    };
    
    return types[type] || type;
  };
  
  if (!hasAccess) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Card sx={{ mb: 3 }}>
          <Typography variant="h5" color="error">
            Access Denied
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            You do not have permission to view this employee's information.
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 2, mb: 1 }}>
            <Button
              onClick={() => navigate(-1)}
              variant="outlined"
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
          </Stack>
        </Card>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 3 }}>
        <Box gridColumn="span 12" sx={{ mt: 4, mb: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center">
              <IconButton onClick={handleBack} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" component="h1">
                Employee Details
              </Typography>
            </Box>
            
            {isHR && (
              <Stack direction="row" spacing={2} sx={{ mt: 2, mb: 1 }}>
                <Button
                  onClick={() => navigate(-1)}
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                >
                  Back
                </Button>
                {employee && (
                  <Button
                    onClick={() => navigate(`/employees/edit/${employee.id}`)}
                    variant="contained"
                    color="primary"
                    startIcon={<EditIcon />}
                  >
                    Edit Employee
                  </Button>
                )}
              </Stack>
            )}
          </Box>
          
          {loading && <LinearProgress sx={{ mb: 3 }} />}
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {employee && (
            <>
              {/* Summary Cards */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
                <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <ContactMailIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">Basic Info</Typography>
                      </Box>
                      <List dense>
                        <ListItem>
                          <ListItemText 
                            primary="Employee ID" 
                            secondary={employee.employee_no || '—'} 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Full Name" 
                            secondary={employee.first_name_en} 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Email" 
                            secondary={employee.email || '—'} 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Status" 
                            secondary={
                              <Chip 
                                label={employee.status === 'active' ? 'Active' : 'Inactive'} 
                                color={employee.status === 'active' ? 'success' : 'error'}
                                size="small"
                              />
                            } 
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Box>
                
                <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <WorkIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">Employment</Typography>
                      </Box>
                      <List dense>
                        <ListItem>
                          <ListItemText 
                            primary="Department" 
                            secondary={'N/A'} 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Job Title" 
                            secondary={employee.job_title || '—'} 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Hire Date" 
                            secondary={'N/A'} 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Termination Date" 
                            secondary={'Not terminated'} 
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Box>
                
                <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <PaymentIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">Current Salary</Typography>
                      </Box>
                      {getCurrentSalaryProfile() ? (
                        <List dense>
                          <ListItem>
                            <ListItemText 
                              primary="Base Salary" 
                              secondary={formatCurrency(getCurrentSalaryProfile()?.base_salary_vnd || 0, 'VND')} 
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText 
                              primary="Contract Type" 
                              secondary={getContractTypeDisplay(getCurrentSalaryProfile()?.contract_type || '')} 
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText 
                              primary="Effective From" 
                              secondary={formatDate(getCurrentSalaryProfile()?.effective_from || '')} 
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText 
                              primary="Standard Work Days" 
                              secondary={getCurrentSalaryProfile()?.standard_work_days || '—'} 
                            />
                          </ListItem>
                        </List>
                      ) : (
                        <Box textAlign="center" py={2}>
                          <Typography variant="body2" color="textSecondary">
                            No active salary profile
                          </Typography>
                          {isHR && (
                            <Button
                              startIcon={<PersonAddIcon />}
                              variant="outlined"
                              size="small"
                              sx={{ mt: 1 }}
                              onClick={handleAddSalaryProfile}
                            >
                              Add Salary Profile
                            </Button>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              </Box>
              
              {/* Tabs Navigation */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="employee tabs">
                  <Tab label="Personal Details" icon={<ContactMailIcon />} iconPosition="start" {...a11yProps(0)} />
                  <Tab label="Salary History" icon={<HistoryIcon />} iconPosition="start" {...a11yProps(1)} />
                  <Tab label="Payroll History" icon={<PaymentIcon />} iconPosition="start" {...a11yProps(2)} />
                </Tabs>
              </Box>
              
              {/* Tab Panels */}
              <TabPanel value={activeTab} index={0}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <Box sx={{ flex: '1 1 500px', minWidth: '500px' }}>
                    <Typography variant="h6" gutterBottom>
                      Personal Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <Typography variant="body2" color="textSecondary">
                          Gender
                        </Typography>
                        <Typography variant="body1">
                          {employee.job_title?.includes('|') ? employee.job_title.split('|')[0].trim() : 'N/A'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ flex: '1 1 100%', width: '100%' }}>
                        <Typography variant="body2" color="textSecondary">
                          Address
                        </Typography>
                        <Typography variant="body1">
                          {'N/A'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <Typography variant="body2" color="textSecondary">
                          Phone
                        </Typography>
                        <Typography variant="body1">
                          {'N/A'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <Typography variant="body2" color="textSecondary">
                          Emergency Contact
                        </Typography>
                        <Typography variant="body1">
                          {'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  <Box sx={{ flex: '1 1 500px', minWidth: '500px' }}>
                    <Typography variant="h6" gutterBottom>
                      Official Documents
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <Typography variant="body2" color="textSecondary">
                          ID Number
                        </Typography>
                        <Typography variant="body1">
                          {'N/A'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <Typography variant="body2" color="textSecondary">
                          Tax ID
                        </Typography>
                        <Typography variant="body1">
                          {employee.tax_code || 'N/A'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <Typography variant="body2" color="textSecondary">
                          Social Insurance Number
                        </Typography>
                        <Typography variant="body1">
                          {employee.si_code || 'N/A'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <Typography variant="body2" color="textSecondary">
                          National ID
                        </Typography>
                        <Typography variant="body1">
                          {employee.national_id || 'N/A'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ flex: '1 1 100%', width: '100%' }}>
                        <Typography variant="body2" color="textSecondary">
                          Bank Account
                        </Typography>
                        <Typography variant="body1">
                          {employee.bank_account_no || 'N/A'} ({employee.bank_name || '—'})
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  {/* Additional Employment Information */}
                  <Box sx={{ flex: '1 1 100%', width: '100%' }}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Additional Employment Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <Typography variant="body2" color="textSecondary">
                          Manager
                        </Typography>
                        <Typography variant="body1">
                          {'N/A'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <Typography variant="body2" color="textSecondary">
                          Work Location
                        </Typography>
                        <Typography variant="body1">
                          {'N/A'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <Typography variant="body2" color="textSecondary">
                          Remote Work
                        </Typography>
                        <Typography variant="body1">
                          {'No'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" color="textSecondary">
                          Notes
                        </Typography>
                        <Typography variant="body1">
                          {'No notes'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </TabPanel>
              
              <TabPanel value={activeTab} index={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6">
                    Salary Profile History
                  </Typography>
                  
                  {isHR && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PersonAddIcon />}
                      onClick={handleAddSalaryProfile}
                    >
                      Add Salary Profile
                    </Button>
                  )}
                </Box>
                
                {salaryProfiles.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Effective From</TableCell>
                          <TableCell>Effective To</TableCell>
                          <TableCell>Base Salary</TableCell>
                          <TableCell>Contract Type</TableCell>
                          <TableCell>Working Days</TableCell>
                          <TableCell>Status</TableCell>
                          {isHR && <TableCell align="center">Actions</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {salaryProfiles.map((profile) => {
                          const today = new Date();
                          const effectiveFrom = new Date(profile.effective_from);
                          const effectiveTo = profile.effective_to ? new Date(profile.effective_to) : null;
                          
                          // Determine if profile is active
                          const isActive = effectiveFrom <= today && (!effectiveTo || effectiveTo >= today);
                          
                          // Determine if profile is future
                          const isFuture = effectiveFrom > today;
                          
                          // Determine status
                          let status = 'Expired';
                          let statusColor: 'default' | 'success' | 'info' = 'default';
                          
                          if (isActive) {
                            status = 'Active';
                            statusColor = 'success';
                          } else if (isFuture) {
                            status = 'Future';
                            statusColor = 'info';
                          }
                          
                          return (
                            <TableRow key={profile.id} hover>
                              <TableCell>{formatDate(profile.effective_from)}</TableCell>
                              <TableCell>{profile.effective_to ? formatDate(profile.effective_to) : '—'}</TableCell>
                              <TableCell>{formatCurrency(profile.base_salary_vnd, 'VND')}</TableCell>
                              <TableCell>{profile.contract_type ? getContractTypeDisplay(profile.contract_type) : '—'}</TableCell>
                              <TableCell>{profile.standard_work_days}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={status} 
                                  color={statusColor}
                                  size="small" 
                                />
                              </TableCell>
                              {isHR && (
                                <TableCell align="center">
                                  <Tooltip title="View Details">
                                    <IconButton 
                                      size="small" 
                                      component={Link} 
                                      to={`/payroll/salary-profiles/${profile.id}`}
                                      color="primary"
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  
                                  <Tooltip title="Edit Profile">
                                    <IconButton 
                                      size="small" 
                                      component={Link} 
                                      to={`/payroll/salary-profiles/edit/${profile.id}`}
                                      color="primary"
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">
                    No salary profiles found for this employee.
                  </Alert>
                )}
              </TabPanel>
              
              <TabPanel value={activeTab} index={2}>
                <Typography variant="h6" gutterBottom>
                  Payroll History
                </Typography>
                
                {payrollHistory.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Period</TableCell>
                          <TableCell>Working Days</TableCell>
                          <TableCell>Gross Income</TableCell>
                          <TableCell>Deductions</TableCell>
                          <TableCell>Net Income</TableCell>
                          <TableCell>Status</TableCell>
                          {isPayroll && <TableCell align="center">Actions</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {payrollHistory.map((item) => {
                          // Calculate total deductions
                          const totalDeductions = 
                            (item.si_employee_vnd || 0) + 
                            (item.hi_employee_vnd || 0) + 
                            (item.ui_employee_vnd || 0) + 
                            (item.pit_vnd || 0) + 
                            (item.advance_payment_vnd || 0) + 
                            (item.other_deductions_vnd || 0);
                          
                          return (
                            <TableRow key={item.id} hover>
                              <TableCell>{item.period}</TableCell>
                              <TableCell>{item.work_days}</TableCell>
                              <TableCell>{formatCurrency(item.gross_income_vnd || 0, 'VND')}</TableCell>
                              <TableCell>{formatCurrency(totalDeductions, 'VND')}</TableCell>
                              <TableCell>{formatCurrency(item.net_income_vnd || 0, 'VND')}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={'DRAFT'}
                                  color={'default'}
                                  size="small" 
                                />
                              </TableCell>
                              {isPayroll && (
                                <TableCell align="center">
                                  <Tooltip title="View Details">
                                    <IconButton 
                                      size="small" 
                                      component={Link} 
                                      to={`/payroll/items/${item.id}`}
                                      color="primary"
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  
                                  {false && (
                                    <Tooltip title="Edit Payroll Item">
                                      <IconButton 
                                        size="small" 
                                        component={Link} 
                                        to={`/payroll/items/edit/${item.id}`}
                                        color="primary"
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">
                    No payroll history found for this employee.
                  </Alert>
                )}
              </TabPanel>
            </>
          )}
        </Box>
        
        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          title="Delete Employee"
          content={`Are you sure you want to delete ${employee?.first_name_en} ${employee?.last_name_en}? This action cannot be undone and will affect all related payroll records.`}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          confirmColor="error"
          confirmText="Delete"
        />
      </Box>
    </Container>
    );
  };

  export default EmployeeDetail;
